import { useState } from 'react';
import { useCountdown } from 'src/common/useCountdown';
import {
  Box,
  Button,
  CloseButton,
  Flex,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useClipboard } from '@mantine/hooks';
import { IconMail, IconCheck, IconCopy } from '@tabler/icons-react';
import type { EmailContentResponse } from 'src/meeting-note/hooks/useGenerateEmailContent';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { buildMailtoUrl, truncateForMailto } from 'src/common/utils/email/mailto';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

const MAILTO_MAX_BODY_LENGTH = 1800;
const MAILTO_HINT_DURATION_SECONDS = 20;

export type EditableEmailFields = {
  recipientEmail: string;
  subject: string;
  body: string;
};

type EmailContentModalProps = {
  opened: boolean;
  onClose: () => void;
  content: EmailContentResponse | null;
  onDelete: () => void;
  onSave: (fields: EditableEmailFields) => Promise<void>;
  isDeleting: boolean;
  isSaving: boolean;
  clientName?: string;
  documentLabel?: string;
};

type CopyButtonOverlayProps = {
  copied: boolean;
  onCopy: () => void;
};

function CopyButtonOverlay({ copied, onCopy }: CopyButtonOverlayProps) {
  return (
    <Button
      variant="subtle"
      color={copied ? 'teal' : 'gray'}
      size="compact-sm"
      pos="absolute"
      top={12}
      right={12}
      style={{ zIndex: 1 }}
      leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
      onClick={onCopy}
    >
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

type InfoPanelProps = {
  clientName?: string;
  documentLabel?: string;
  bodyTooLong: boolean;
  isDirty: boolean;
  isSaving: boolean;
  onOpenInClient: () => void;
  onSaveClick: () => void;
  onDeleteClick: () => void;
};

function InfoPanel({
  clientName,
  documentLabel,
  bodyTooLong,
  isDirty,
  isSaving,
  onOpenInClient,
  onSaveClick,
  onDeleteClick,
}: InfoPanelProps) {
  const [showHint, setShowHint] = useState(false);
  const hintCountdown = useCountdown(MAILTO_HINT_DURATION_SECONDS, showHint, () =>
    setShowHint(false),
  );

  const handleOpenInClient = () => {
    onOpenInClient();
    setShowHint(true);
  };

  return (
    <Flex
      direction="column"
      flex={2}
      p="xl"
      bg="white"
      style={{
        borderTopLeftRadius: 'var(--mantine-radius-md)',
        borderBottomLeftRadius: 'var(--mantine-radius-md)',
      }}
    >
      <Box>
        <Title order={4} fw={FONT_WEIGHTS.semibold}>
          Email draft{clientName ? ` for ${clientName}` : ''}
        </Title>
        {documentLabel && (
          <>
            <Text size="sm" c="dimmed" mt="xs">
              Prepared from:
            </Text>
            <Text size="sm" mt="xxxs">
              {documentLabel}
            </Text>
          </>
        )}
      </Box>

      <Box flex={1} pos="relative">
        {showHint && hintCountdown > 0 && (
          <Text size="sm" c="dimmed" pos="absolute" bottom={0} left={0} right={0}>
            Your email app should now be opening with a draft message. If nothing appears, your
            computer may not have a default email application set, or you may be using webmail in
            your browser. You can use the{' '}
            <Text fw={FONT_WEIGHTS.bold} span>
              Copy
            </Text>{' '}
            buttons on each field to paste the content into your email instead.
          </Text>
        )}
      </Box>

      <Stack gap="xs" mt="md">
        <Button
          variant="filled-pill"
          fullWidth
          size="lg"
          leftSection={<IconMail size={18} />}
          onClick={handleOpenInClient}
          disabled={bodyTooLong}
        >
          Open in email client
        </Button>
        <Group grow>
          <Button variant="outline" onClick={onSaveClick} disabled={!isDirty} loading={isSaving}>
            Save draft
          </Button>
          <Button variant="subtle" color="red" onClick={onDeleteClick}>
            Delete draft
          </Button>
        </Group>
      </Stack>
    </Flex>
  );
}

type ContentPanelProps = {
  recipientEmail: string;
  subject: string;
  body: string;
  onRecipientChange: (value: string) => void;
  onSubjectChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onClose: () => void;
};

function ContentPanel({
  recipientEmail,
  subject,
  body,
  onRecipientChange,
  onSubjectChange,
  onBodyChange,
  onClose,
}: ContentPanelProps) {
  const clipboard = useClipboard();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = (field: string, value: string) => {
    clipboard.copy(value);
    setCopiedField(field);
  };

  return (
    <Flex
      direction="column"
      flex={3}
      bg="gray.0"
      p="xl"
      pos="relative"
      mih={0}
      style={{
        borderTopRightRadius: 'var(--mantine-radius-md)',
        borderBottomRightRadius: 'var(--mantine-radius-md)',
        overflow: 'hidden',
      }}
    >
      <CloseButton
        c="var(--mantine-color-gray-5)"
        onClick={onClose}
        pos="absolute"
        top={16}
        right={16}
      />

      <Text size="sm" c="dimmed" mb="md">
        Edit fields as needed, then copy or open in your email client
      </Text>

      <Stack gap="sm" flex={1} mih={0} style={{ overflow: 'hidden' }}>
        <Paper bg="white" p="md" radius="md" pos="relative">
          <CopyButtonOverlay
            copied={copiedField === 'to'}
            onCopy={() => handleCopy('to', recipientEmail)}
          />
          <TextInput
            label="To"
            value={recipientEmail}
            onChange={e => onRecipientChange(e.currentTarget.value)}
            size="sm"
            styles={{ label: { fontWeight: FONT_WEIGHTS.semibold } }}
          />
        </Paper>

        <Paper bg="white" p="md" radius="md" pos="relative">
          <CopyButtonOverlay
            copied={copiedField === 'subject'}
            onCopy={() => handleCopy('subject', subject)}
          />
          <TextInput
            label="Subject"
            value={subject}
            onChange={e => onSubjectChange(e.currentTarget.value)}
            size="sm"
            styles={{ label: { fontWeight: FONT_WEIGHTS.semibold } }}
          />
        </Paper>

        <Paper
          bg="white"
          p="md"
          radius="md"
          pos="relative"
          flex={1}
          mih={0}
          style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <CopyButtonOverlay
            copied={copiedField === 'body'}
            onCopy={() => handleCopy('body', body)}
          />
          <Textarea
            label="Body"
            value={body}
            onChange={e => onBodyChange(e.currentTarget.value)}
            size="sm"
            rows={12}
            resize="vertical"
            styles={{ label: { fontWeight: FONT_WEIGHTS.semibold } }}
          />
        </Paper>
      </Stack>
    </Flex>
  );
}

type EmailContentModalInnerProps = {
  content: EmailContentResponse;
  onClose: () => void;
  onDelete: () => void;
  onSave: (fields: EditableEmailFields) => Promise<void>;
  isDeleting: boolean;
  isSaving: boolean;
  clientName?: string;
  documentLabel?: string;
};

function EmailContentModalInner({
  content,
  onClose,
  onDelete,
  onSave,
  isDeleting,
  isSaving,
  clientName,
  documentLabel,
}: EmailContentModalInnerProps) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState(content.recipientEmail);
  const [subject, setSubject] = useState(content.subject);
  const [body, setBody] = useState(content.body);

  const isDirty =
    recipientEmail !== content.recipientEmail ||
    subject !== content.subject ||
    body !== content.body;

  const bodyTooLong = body.length > MAILTO_MAX_BODY_LENGTH;

  const handleOpenInClient = () => {
    const truncatedBody = truncateForMailto(body);
    const url = buildMailtoUrl(recipientEmail, subject, truncatedBody);
    window.open(url, '_self');
  };

  const handleSave = () => {
    onSave({ recipientEmail, subject, body });
  };

  return (
    <>
      <Flex direction="row" mih={400} mah="80vh">
        <InfoPanel
          clientName={clientName}
          documentLabel={documentLabel}
          bodyTooLong={bodyTooLong}
          isDirty={isDirty}
          isSaving={isSaving}
          onOpenInClient={handleOpenInClient}
          onSaveClick={handleSave}
          onDeleteClick={() => setDeleteModalOpen(true)}
        />
        <ContentPanel
          recipientEmail={recipientEmail}
          subject={subject}
          body={body}
          onRecipientChange={setRecipientEmail}
          onSubjectChange={setSubject}
          onBodyChange={setBody}
          onClose={onClose}
        />
      </Flex>

      <ConfirmDeleteModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => {
          setDeleteModalOpen(false);
          onDelete();
        }}
        title="Delete email"
        message="Are you sure you want to delete this email? You will need to regenerate it if you want to send it later."
        isLoading={isDeleting}
      />
    </>
  );
}

export default function EmailContentModal({
  opened,
  onClose,
  content,
  onDelete,
  onSave,
  isDeleting,
  isSaving,
  clientName,
  documentLabel,
}: EmailContentModalProps) {
  if (!content) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      centered
      size="xxl"
      variant="multi"
    >
      <EmailContentModalInner
        key={content.id}
        content={content}
        onClose={onClose}
        onDelete={onDelete}
        onSave={onSave}
        isDeleting={isDeleting}
        isSaving={isSaving}
        clientName={clientName}
        documentLabel={documentLabel}
      />
    </Modal>
  );
}
